import React from "react";
import { Collapse, Image } from "antd";
import moment from "moment";
import {
  UserOutlined,
  FileSearchOutlined,
  BulbOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import Empty from "../elements/empty";

interface MedicalRecordItem {
  id: number | string;
  date: string;
  prescription?: string;
  doctorName?: string;
  diagnosis?: string;
  recommendations?: string;
  remarks?: string;
}

// Short single-value fields (a name) — compact icon + label + value, one line.
function RecordField({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-primary-blue shadow-sm">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="m-0 text-xs text-slate-500">{label}</p>
        <p className="m-0 truncate text-sm font-medium text-slate-800">
          {value}
        </p>
      </div>
    </div>
  );
}

// Prose fields (diagnosis, recommendations, remarks) — these run several
// sentences long. The old layout put them in a fixed-width table column
// with `whitespace-nowrap`, which just clipped the text at the edge of the
// card instead of wrapping it — nothing after that point was readable at
// all without the browser's own horizontal scrollbar. Full-width block with
// normal wrapping fixes that outright.
function RecordTextBlock({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3">
      <div className="mb-1.5 flex items-center gap-2 text-slate-500">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-white text-primary-blue shadow-sm">
          {icon}
        </span>
        <span className="text-xs font-semibold">{label}</span>
      </div>
      <p className="m-0 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
        {value}
      </p>
    </div>
  );
}

export default function PatientRecords({ data }: { data: MedicalRecordItem[] }) {
  if (!data || data.length === 0) {
    return (
      <Empty
        title="Medical Records"
        imageStyle="h-16"
        titleStyle="text-xs"
        description="No medical record available"
      />
    );
  }

  return (
    <Collapse
      accordion
      expandIconPosition="end"
      className="medical-record-accordion"
      items={data.map((item) => ({
        key: item.id,
        label: moment(item.date).format("Do MMMM YYYY"),
        children: (
          <div className="space-y-3">
            {item.prescription && (
              <div>
                <p className="mb-2 text-xs font-semibold text-slate-500">
                  Prescription
                </p>
                <Image
                  width={96}
                  height={96}
                  src={item.prescription}
                  alt="Prescription"
                  className="rounded-lg object-cover"
                />
              </div>
            )}
            <RecordField
              icon={<UserOutlined />}
              label="Doctor"
              value={item.doctorName || "n/a"}
            />
            <RecordTextBlock
              icon={<FileSearchOutlined />}
              label="Diagnosis"
              value={item.diagnosis || "n/a"}
            />
            <RecordTextBlock
              icon={<BulbOutlined />}
              label="Recommendations"
              value={item.recommendations || "n/a"}
            />
            <RecordTextBlock
              icon={<MessageOutlined />}
              label="Remarks"
              value={item.remarks || "n/a"}
            />
          </div>
        ),
      }))}
    />
  );
}
